import type { AgentBuilderTriggerType } from "@app/components/agent_builder/agentBuilderFormSchema";
import { ScheduleEditionSheetContent } from "@app/components/agent_builder/triggers/schedule/ScheduleEditionSheet";
import { TriggerSelectionPageContent } from "@app/components/agent_builder/triggers/TriggerSelectionPage";
import type { SheetMode } from "@app/components/agent_builder/triggers/TriggerViewsSheet";
import { WebhookEditionSheetContent } from "@app/components/agent_builder/triggers/webhook/WebhookEditionSheet";
import { AgentDetailsButtonBar } from "@app/components/assistant/details/AgentDetailsButtonBar";
import { EditedDot } from "@app/components/assistant/details/DetailsSectionHeading";
import {
  useAgentSuggestionPreview,
  useEditedAgentSections,
} from "@app/components/assistant/details/SuggestionPreviewContext";
import { AgentEditorsTab } from "@app/components/assistant/details/tabs/AgentEditorsTab";
import { AgentInfoTab } from "@app/components/assistant/details/tabs/AgentInfoTab";
import { AgentInsightsTab } from "@app/components/assistant/details/tabs/AgentInsightsTab";
import { AgentMemoryTab } from "@app/components/assistant/details/tabs/AgentMemoryTab";
import { AgentTriggersTab } from "@app/components/assistant/details/tabs/AgentTriggersTab";
import { useTriggerSheetState } from "@app/components/assistant/details/useTriggerSheetState";
import { RestoreAgentDialog } from "@app/components/assistant/RestoreAgentDialog";
import { FormProvider } from "@app/components/sparkle/FormProvider";
import { isServerSideMCPServerConfigurationWithName } from "@app/lib/actions/types/guards";
import { AGENT_MEMORY_SERVER_NAME } from "@app/lib/api/actions/servers/agent_memory/metadata";
import { ASSISTANT_EMAIL_SUBDOMAIN } from "@app/lib/api/assistant/email/constants";
import { FICHE_ASSISTANT_AVEC_EMAIL } from "@app/lib/compta/branding";
import { ongletInitialDeLaFiche } from "@app/lib/compta/fiche_agent";
import { formatDate } from "@app/lib/i18n/format";
import { useAppRouter } from "@app/lib/platform";
import { useAgentSuggestionsPreview } from "@app/lib/swr/agent_suggestions";
import { useAgentConfiguration } from "@app/lib/swr/assistants";
import { useSpaces } from "@app/lib/swr/spaces";
import { useEmailAgentFooter } from "@app/lib/swr/user";
import { useWebhookSourceViewsFromSpaces } from "@app/lib/swr/webhook_source";
import { removeParamFromRouter } from "@app/lib/utils/router_util";
import type { AgentConfigurationScope } from "@app/types/assistant/agent";
import { GLOBAL_AGENTS_SID } from "@app/types/assistant/assistant";
import type { TriggerType } from "@app/types/assistant/triggers";
import { assertNever } from "@app/types/shared/utils/assert_never";
import type { WebhookSourceViewType } from "@app/types/triggers/webhooks";
import type { UserType, WorkspaceType } from "@app/types/user";
import { isManager } from "@app/types/user";
import {
  ArrowLeft,
  Avatar,
  BarChart01,
  Bell01,
  Brain,
  Button,
  Chip,
  ContentMessage,
  ContentMessageAction,
  ContentMessageInline,
  cn,
  InfoCircle,
  Lock01,
  Mail01,
  Markdown,
  RefreshCw02,
  SheetContainer,
  SheetHeader,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Users01,
  XClose,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useCallback, useEffect, useState } from "react";

function triggerTypeToBuilderType(
  trigger: TriggerType
): AgentBuilderTriggerType {
  switch (trigger.kind) {
    case "schedule":
      return {
        sId: trigger.sId,
        status: trigger.status,
        name: trigger.name,
        kind: "schedule",
        customPrompt: trigger.customPrompt,
        naturalLanguageDescription: trigger.naturalLanguageDescription,
        configuration: trigger.configuration,
        editor: trigger.editor,
        executionMode: trigger.executionMode,
        spaceId: trigger.spaceId,
      };
    case "webhook":
      return {
        sId: trigger.sId,
        status: trigger.status,
        name: trigger.name,
        kind: "webhook",
        customPrompt: trigger.customPrompt,
        naturalLanguageDescription: trigger.naturalLanguageDescription,
        configuration: trigger.configuration,
        editor: trigger.editor,
        webhookSourceViewId: trigger.webhookSourceViewId,
        executionPerDayLimitOverride: trigger.executionPerDayLimitOverride,
        executionMode: trigger.executionMode,
        spaceId: trigger.spaceId,
      };
    default:
      assertNever(trigger);
  }
}

const EMAIL_AGENTS_DOCS_URL =
  "https://docs.dust.tt/docs/user-documentation/agents/integrations/send-and-forward-email-to-agents";

export const SCOPE_INFO: Record<
  AgentConfigurationScope,
  {
    label: MessageDescriptor;
    color: "success" | "info" | "highlight" | "primary";
    icon?: typeof Users01 | undefined;
    text: MessageDescriptor;
  }
> = {
  global: {
    label: msg`Default agent`,
    color: "primary",
    text: msg`Default agents provided by Dust.`,
  },
  hidden: {
    label: msg`Not published`,
    color: "primary",
    text: msg`Hidden agents.`,
  },
  visible: {
    label: msg`Published`,
    color: "success",
    text: msg`Visible agents.`,
  },
} as const;

interface AgentDetailsBodyProps {
  owner: WorkspaceType;
  agentId: string | null;
  user: UserType;
  isInSidePanel?: boolean;
}

// compta : onglet demande par l'adresse (agentDetailsTab), « info » sinon.
function ongletDeLAdresse() {
  return ongletInitialDeLaFiche(
    typeof window === "undefined"
      ? undefined
      : new URLSearchParams(window.location.search).get("agentDetailsTab")
  );
}

export function AgentDetailsBody({
  agentId,
  owner,
  user,
  isInSidePanel = false,
}: AgentDetailsBodyProps) {
  const { t } = useLingui();
  // compta : onglet de depart lu dans l'adresse (agentDetailsTab), pour ouvrir les declencheurs.
  const [selectedTab, setSelectedTab] = useState<
    "info" | "insights" | "editors" | "agent_memory" | "triggers"
  >(ongletDeLAdresse);
  const router = useAppRouter();
  const [triggerEditMode, setTriggerEditMode] = useState<SheetMode | null>(
    null
  );
  const { showEmailFooter, dismissFooter, isDismissing } = useEmailAgentFooter({
    disabled: !agentId,
  });

  const {
    agentConfiguration: fetchedAgentConfiguration,
    isAgentConfigurationLoading,
    isAgentConfigurationValidating,
    isAgentConfigurationError,
  } = useAgentConfiguration({
    workspaceId: owner.sId,
    agentConfigurationId: agentId,
  });

  const previewSuggestions = useAgentSuggestionPreview();
  const { preview } = useAgentSuggestionsPreview({
    agent: fetchedAgentConfiguration,
    suggestions: previewSuggestions,
  });
  const agentConfiguration =
    fetchedAgentConfiguration && preview
      ? { ...fetchedAgentConfiguration, ...preview.fields }
      : fetchedAgentConfiguration;
  const previewedCapabilities = preview?.capabilities ?? null;

  // Fetch webhook source views when triggers tab is active so they're ready
  // when the user clicks edit on a webhook trigger.
  const isTriggersTabActive = selectedTab === "triggers" && !!agentId;

  const { spaces } = useSpaces({
    workspaceId: owner.sId,
    kinds: "all",
    disabled: !isTriggersTabActive,
  });

  const { webhookSourceViews } = useWebhookSourceViewsFromSpaces(
    owner,
    spaces,
    !isTriggersTabActive
  );

  const handleAddTrigger = useCallback(() => {
    setTriggerEditMode({ type: "add" });
  }, []);

  const handleEditTrigger = useCallback(
    (trigger: TriggerType) => {
      const builderTrigger = triggerTypeToBuilderType(trigger);
      const wsv =
        trigger.kind === "webhook" && trigger.webhookSourceViewId
          ? (webhookSourceViews.find(
              (v) => v.sId === trigger.webhookSourceViewId
            ) ?? null)
          : null;
      setTriggerEditMode({
        type: "edit",
        trigger: builderTrigger,
        webhookSourceView: wsv,
      });
    },
    [webhookSourceViews]
  );

  useEffect(() => {
    // Reset to info tab and close trigger editing when we open/close the modal
    // compta : sauf si l'adresse demande un onglet (agentDetailsTab), lu une fois puis retire de
    // l'adresse pour que la fiche suivante reparte sur « info ».
    setSelectedTab(ongletDeLAdresse());
    setTriggerEditMode(null);
    if (agentId && router.query.agentDetailsTab !== undefined) {
      void removeParamFromRouter(router, "agentDetailsTab");
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- compta : l'onglet de l'adresse se lit a l'ouverture, pas a chaque navigation
  }, [agentId]);

  const isGlobalAgent = Object.values(GLOBAL_AGENTS_SID).includes(
    agentConfiguration?.sId as GLOBAL_AGENTS_SID
  );

  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const showEditorsTabs =
    agentId != null &&
    !isGlobalAgent &&
    agentConfiguration?.status === "active";
  // The triggers tab only lists the caller's own triggers, and creating one needs `read` on the
  // agent.
  const showTriggersTabs =
    agentId != null &&
    agentConfiguration?.status === "active" &&
    agentConfiguration.canRead;
  const showAgentMemory = !!agentConfiguration?.actions.find((arg) =>
    isServerSideMCPServerConfigurationWithName(arg, AGENT_MEMORY_SERVER_NAME)
  );

  const showInsightsTabs =
    agentId != null && (agentConfiguration?.canEdit || isManager(owner));

  const editedSections = useEditedAgentSections();

  const agentEmail = `${agentConfiguration?.name}@${ASSISTANT_EMAIL_SUBDOMAIN}`;

  const DescriptionSection = () => {
    const lastAuthor = agentConfiguration?.lastAuthors?.[0];
    const editedDate =
      agentConfiguration?.versionCreatedAt &&
      formatDate(new Date(agentConfiguration.versionCreatedAt), {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });

    return (
      <div className="flex flex-col items-center gap-4 pt-4">
        <div className="relative flex items-center justify-center">
          <div className="relative flex flex-col items-center gap-2">
            <Avatar
              name={t`Agent avatar`}
              visual={agentConfiguration?.pictureUrl}
              size="xl"
            />
            {agentConfiguration?.status === "active" && (
              <div className="absolute -bottom-3 flex items-center gap-1">
                <Chip
                  size="mini"
                  color={SCOPE_INFO[agentConfiguration.scope].color}
                  icon={SCOPE_INFO[agentConfiguration.scope].icon ?? undefined}
                  label={t(SCOPE_INFO[agentConfiguration.scope].label)}
                  className="shadow-sm"
                />
                {editedSections.has("scope") && <EditedDot />}
              </div>
            )}
          </div>
        </div>

        {/* Title and edit info */}
        <div className="flex flex-col items-center gap-1">
          <div className="flex items-center gap-2">
            <h2 className="text-xl font-semibold text-foreground notranslate">
              {agentConfiguration?.name ?? ""}
            </h2>
            {editedSections.has("name") && <EditedDot />}
          </div>
          {editedDate && (
            <p className="text-sm text-muted-foreground">
              {lastAuthor ? (
                <Trans>
                  Last edited: {editedDate} by {lastAuthor}
                </Trans>
              ) : (
                <Trans>Last edited: {editedDate}</Trans>
              )}
            </p>
          )}
        </div>

        {agentConfiguration?.status === "active" && (
          <AgentDetailsButtonBar
            owner={owner}
            agentConfiguration={agentConfiguration}
            isAgentConfigurationValidating={isAgentConfigurationValidating}
          />
        )}

        {agentConfiguration?.status === "archived" && (
          <>
            <ContentMessage
              title={t`This agent has been archived.`}
              variant="warning"
              icon={InfoCircle}
              size="sm"
            >
              <Trans context="archived agent">
                It is no longer active and cannot be used.
              </Trans>
              <br />
              <div className="mt-2">
                <Button
                  variant="outline"
                  label={t`Restore`}
                  onClick={() => {
                    setShowRestoreModal(true);
                  }}
                  className="mt-2"
                  icon={RefreshCw02}
                />
              </div>
            </ContentMessage>

            <RestoreAgentDialog
              owner={owner}
              isOpen={showRestoreModal}
              agentConfiguration={agentConfiguration}
              onClose={() => {
                setShowRestoreModal(false);
              }}
            />
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {isAgentConfigurationLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : triggerEditMode && agentConfiguration ? (
        <TriggerEditView
          owner={owner}
          agentConfigurationId={agentConfiguration.sId}
          mode={triggerEditMode}
          webhookSourceViews={webhookSourceViews}
          onClose={() => setTriggerEditMode(null)}
        />
      ) : (
        <>
          <SheetHeader
            className={cn(
              "flex flex-col gap-5 text-sm text-foreground",
              isInSidePanel && "bg-transparent"
            )}
            hideButton={isInSidePanel}
          >
            <DescriptionSection />
          </SheetHeader>
          <SheetContainer className="pb-4">
            {showEditorsTabs ||
            showAgentMemory ||
            showInsightsTabs ||
            showTriggersTabs ? (
              <Tabs value={selectedTab}>
                <TabsList border={false}>
                  <TabsTrigger
                    value="info"
                    label={t`Info`}
                    icon={InfoCircle}
                    onClick={() => setSelectedTab("info")}
                  />
                  {showInsightsTabs && (
                    <TabsTrigger
                      value="insights"
                      label={t`Insights`}
                      icon={BarChart01}
                      onClick={() => setSelectedTab("insights")}
                    />
                  )}
                  {showTriggersTabs && (
                    <TabsTrigger
                      value="triggers"
                      label={t`Triggers`}
                      icon={Bell01}
                      onClick={() => setSelectedTab("triggers")}
                    />
                  )}
                  {showEditorsTabs && (
                    <TabsTrigger
                      value="editors"
                      label={t`Editors`}
                      icon={Users01}
                      iconRight={
                        editedSections.has("editors") ? (
                          <EditedDot />
                        ) : undefined
                      }
                      onClick={() => setSelectedTab("editors")}
                    />
                  )}
                  {showAgentMemory && (
                    <TabsTrigger
                      value="agent_memory"
                      label={t`Memory`}
                      icon={Brain}
                      onClick={() => setSelectedTab("agent_memory")}
                    />
                  )}
                </TabsList>
                {agentConfiguration && (
                  <div className="mt-4">
                    <TabsContent value="info">
                      <AgentInfoTab
                        agentConfiguration={agentConfiguration}
                        previewedCapabilities={previewedCapabilities}
                        owner={owner}
                      />
                    </TabsContent>
                    {showInsightsTabs && (
                      <TabsContent value="insights">
                        <AgentInsightsTab
                          owner={owner}
                          agentConfiguration={agentConfiguration}
                        />
                      </TabsContent>
                    )}
                    {showTriggersTabs && (
                      <TabsContent value="triggers">
                        <AgentTriggersTab
                          agentConfiguration={agentConfiguration}
                          owner={owner}
                          onEditTrigger={handleEditTrigger}
                          onAddTrigger={handleAddTrigger}
                        />
                      </TabsContent>
                    )}
                    <TabsContent value="editors">
                      <AgentEditorsTab
                        key={agentConfiguration.sId}
                        owner={owner}
                        user={user}
                        agentConfiguration={agentConfiguration}
                      />
                    </TabsContent>
                    <TabsContent value="agent_memory">
                      <AgentMemoryTab
                        owner={owner}
                        agentConfiguration={agentConfiguration}
                      />
                    </TabsContent>
                  </div>
                )}
              </Tabs>
            ) : agentConfiguration ? (
              <AgentInfoTab
                agentConfiguration={agentConfiguration}
                previewedCapabilities={previewedCapabilities}
                owner={owner}
              />
            ) : (
              <div />
            )}
            {isAgentConfigurationError?.error.type ===
              "agent_configuration_not_found" && (
              <ContentMessage title={t`Not available`} icon={Lock01} size="md">
                <Trans>This agent is not available.</Trans>
              </ContentMessage>
            )}
          </SheetContainer>
          {/* Lot E1b : le bandeau « Email this agent » de Dust n'a pas d'objet ici. */}
          {FICHE_ASSISTANT_AVEC_EMAIL &&
            showEmailFooter &&
            agentConfiguration?.status === "active" &&
            agentConfiguration.canRead &&
            !(
              Array.isArray(owner.metadata?.emailBlacklistedAgentIds) &&
              owner.metadata.emailBlacklistedAgentIds.includes(
                agentConfiguration.sId
              )
            ) && (
              <div className="px-5 pb-4">
                <ContentMessageInline
                  variant="primary"
                  icon={Mail01}
                  className="py-1"
                >
                  <Markdown
                    content={t`Email this agent at \`${agentEmail}\`. Forward an email with your instructions and [get its reply](${EMAIL_AGENTS_DOCS_URL}) in your inbox.`}
                    forcedTextSize="text-xs"
                    optimizeForStreaming={false}
                  />
                  <ContentMessageAction
                    variant="ghost"
                    icon={XClose}
                    tooltip={t`Dismiss email tip for all agents`}
                    onClick={dismissFooter}
                    isLoading={isDismissing}
                  />
                </ContentMessageInline>
              </div>
            )}
        </>
      )}
    </>
  );
}

interface TriggerEditViewProps {
  owner: WorkspaceType;
  agentConfigurationId: string;
  mode: SheetMode;
  webhookSourceViews: WebhookSourceViewType[];
  onClose: () => void;
}

function TriggerEditView({
  owner,
  agentConfigurationId,
  mode,
  webhookSourceViews,
  onClose,
}: TriggerEditViewProps) {
  const { t } = useLingui();
  const {
    form,
    currentPageId,
    webhookSourceView,
    editTrigger,
    isEditor,
    isOnSelectionPage,
    pageTitle,
    canUseSelectedExecutionMode,
    handleScheduleSelect,
    handleWebhookSelect,
    handleCancel,
    handleFormSubmit,
  } = useTriggerSheetState({
    owner,
    agentConfigurationId,
    mode,
    webhookSourceViews,
    onSuccess: onClose,
  });

  return (
    <>
      <div className="flex flex-row items-center gap-2 p-5 text-sm text-foreground">
        <Button
          tooltip={t`Back`}
          icon={ArrowLeft}
          variant="ghost"
          size="sm"
          onClick={() => {
            if (!isOnSelectionPage && mode.type !== "edit") {
              handleCancel();
            } else {
              onClose();
            }
          }}
        />
        <h2 className="text-lg font-semibold">{pageTitle}</h2>
      </div>
      <FormProvider form={form} asForm={false}>
        <SheetContainer>
          {currentPageId === "trigger-selection" && (
            <TriggerSelectionPageContent
              onScheduleSelect={handleScheduleSelect}
              onWebhookSelect={handleWebhookSelect}
              webhookSourceViews={webhookSourceViews}
            />
          )}
          {currentPageId === "schedule-edition" && (
            <ScheduleEditionSheetContent
              owner={owner}
              trigger={editTrigger?.kind === "schedule" ? editTrigger : null}
              isEditor={isEditor}
            />
          )}
          {currentPageId === "webhook-edition" && (
            <WebhookEditionSheetContent
              owner={owner}
              trigger={editTrigger?.kind === "webhook" ? editTrigger : null}
              agentConfigurationId={agentConfigurationId}
              webhookSourceView={webhookSourceView}
              isEditor={isEditor}
            />
          )}
        </SheetContainer>
      </FormProvider>
      {!isOnSelectionPage && (
        <div className="flex flex-none justify-end gap-2 border-t border-border p-3">
          <Button
            label={t`Cancel`}
            variant="outline"
            onClick={() => {
              handleCancel();
              onClose();
            }}
          />
          <Button
            label={t`Save`}
            variant="primary"
            disabled={!canUseSelectedExecutionMode}
            onClick={form.handleSubmit(handleFormSubmit)}
          />
        </div>
      )}
    </>
  );
}
